import { createSelector } from "@reduxjs/toolkit";
import {
  getTotalPriceText,
  twoDecimalFormatter,
  emailRegex,
  phoneRegex,
  getRewardsString,
  roundToTwoDecimals,
  getAgentErrorTitle,
  getAgentErrorSubtitle,
  CurrencyFormatters,
  getSubtotalWithAncillariesLegacy,
  IHotelPriceLineItem,
  IconName,
  truncateToTwoDecimals,
  getCheckoutCreditBreakdown,
  TEST_CARD_LAST_FOURS,
  paymentTextConstants,
  getCreditsAvailableByRestrictions,
  getCreditBreakdownAvailableByRestrictions,
  getCreditBreakdownIneligibleByRestrictions,
  isCreditEligibleForRestrictions,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
  DEFAULT_CHECK_IN_MINIMUM_AGE,
} from "halifax";
import {
  HotelBookErrorType,
  FiatPrice,
  RewardsPrice,
  CallState,
  PaymentAmountEnum,
  PaymentSplitRequest,
  PaymentSplitRequestEnum,
  HotelQuoteScheduleRequest,
  PaymentErrorEnum,
  PaymentVerifyResultEnum,
  ModalAlertProperties,
  MODAL_ALERT,
  NO_AVAILABILITY_HOTELS,
  ModalCategoryType,
  ModalScreens,
  RewardsAccountMinimumRequirementState,
  getRewardsAccountMinimumRequirementState,
  getSubtitleTrackingValue,
  PaymentType,
  UserCardPaymentType,
  TypeOfPaymentEnum,
  SplitPaymentType,
  RewardsPaymentType,
  ErrorTitles,
  AvailableProductEnum,
  MerchantOfRecord,
  PaymentSplitRequestV2,
  PaymentV2,
  TravelWalletOfferPayment,
  TravelWalletCreditPayment,
  TravelWalletRestrictedCreditPayment,
  PaymentV2Enum,
  Rewards,
  UserCard,
  Prices,
  CompleteBuyPremierCollectionProperties,
  CancellationPolicyEnum,
  ITrackingProperties,
  ReviewDetailsHotelCheckoutProperties,
  PriceQuotePremierCollectionProperties,
  HotelPriceQuoteScheduleRequestV2,
  AncillaryKindEnum,
  CompleteBuyLifestyleCollectionProperties,
  LodgingCollectionEnum,
  ProductClassEnum,
  RewardsAccount,
  CustomerAccountRole,
  DatelessSearchProperties,
  OffersForProductsRequest,
  PremiumOffersPageProperties,
  RestrictedToProductsEnum,
  CreditCollectionTypeEnum,
  ICorpPerson,
  CreditRestriction,
  StatementCreditDetail,
  StatementCreditProductType,
} from "redmond";
import dayjs from "dayjs";
import {
  ErrorCode,
  Payment,
  PaymentError,
  PaymentOpaqueValue,
  ProductError,
  PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { isCorpTenant } from "@capone/common";
import { LodgingErrorEnum } from "@b2bportal/lodging-api";
import { IStoreState } from "../../../../reducers/types";
import {
  getSearchedNightCount,
  getSelectedLodgingIndex,
} from "../../../availability/reducer";
import {
  getAddedAncillariesPricing,
  getChosenRoomHasPayLater,
  getIsChosenRoomPayLater,
  getPremierCollectionShopChosenProduct,
  getPremierCollectionShopChosenRoomInfo,
  getPremierCollectionShopSelectedAvailability,
  getViewedPremierCollectionDetailsProperties,
  getIsFromPremiumOffers,
  getPersistentLodgingTrackingProperties,
  getSelectedLodgingIsFavorited,
} from "../../../shop/reducer";
import {
  getAgentEmail,
  getAllowRewardsWithPolicy,
  getRewardsAccounts,
  getRewardsAccountWithLargestValue,
  getUserInfo,
} from "../../../rewards/reducer";
import * as textConstants from "./textConstants";
import {
  getFromDate,
  getIsFromHotelDatelessSearch,
  getNightCount,
  getUntilDate,
  getAdultsCount,
  getChildrenCount,
  getPetsCount,
} from "../../../search/reducer";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  DO_NOT_APPLY_OPTION_KEY,
  isLodgingErrorCopyUpdateEnabledSelector,
  selectedCfarIdSelector,
} from "../../../ancillary/reducer";
import { config } from "../../../../api/config";
import { getCreditBreakdown } from "../../../travel-wallet/reducer";
import type { AppliedRestrictedCredit } from "../../actions/actions";

export const getUserPassengers = (state: IStoreState) =>
  state.premierCollectionBook.userPassengers;

export const getUserSelectedTravelerId = (state: IStoreState) =>
  state.premierCollectionBook.userSelectedTravelerId;

export const getUserSelectedTraveler = createSelector(
  getUserSelectedTravelerId,
  getUserPassengers,
  (userSelectedTravelerId, travelers) =>
    travelers.find((t) => t.id === userSelectedTravelerId),
);

export const getIsSelfBooked = createSelector(
  getUserSelectedTraveler,
  getUserInfo,
  (traveler, userInfo) =>
    (traveler as ICorpPerson | undefined)?.isDefaultProfile ??
    (traveler?.givenName === userInfo.firstName &&
      traveler?.surname === userInfo.lastName),
);

export const getUserPassengerCallState = (state: IStoreState) =>
  state.premierCollectionBook.userPassengerCallState;

export const getConfirmationEmail = (state: IStoreState) =>
  state.premierCollectionBook.confirmationEmailAddress;

export const getConfirmationPhoneNumber = (state: IStoreState) =>
  state.premierCollectionBook.confirmationPhoneNumber;

export const getPremierCollectionBookSelectedAvailabilityCheckInMinimumAge =
  createSelector(
    getPremierCollectionShopSelectedAvailability,
    (premierCollectionShopSelectedAvailability): number | undefined =>
      // minimumAge can be returned as "0", so we should check for > 0 to avoid showing it in the UI copy.
      premierCollectionShopSelectedAvailability?.lodging?.checkInInstructions
        ?.minimumAge || DEFAULT_CHECK_IN_MINIMUM_AGE,
  );

export const getPaymentMethods = (state: IStoreState) =>
  state.premierCollectionBook.paymentMethods;

export const getPaymentMethod = (state: IStoreState) =>
  state.premierCollectionBook.paymentMethod;

export const getSelectedPaymentMethodId = (state: IStoreState) =>
  state.premierCollectionBook.selectedPaymentMethodId;

export const getListPaymentMethodsCallState = (state: IStoreState) =>
  state.premierCollectionBook.listPaymentMethodCallState;

export const getDeletePaymentMethodCallState = (state: IStoreState) =>
  state.premierCollectionBook.deletePaymentMethodCallState;

export const getFetchPaymentMethodCallState = (state: IStoreState) =>
  state.premierCollectionBook.fetchPaymentMethodCallState;

export const getVerifyPaymentMethodCallState = (state: IStoreState) =>
  state.premierCollectionBook.verifyPaymentMethodCallState;

export const getSession = (state: IStoreState) =>
  state.premierCollectionBook.session;

export const getPriceQuote = (state: IStoreState) =>
  state.premierCollectionBook.priceQuote;

export const getPricingWithAncillaries = (state: IStoreState) =>
  state.premierCollectionBook.pricingWithAncillaries;

export const getPriceQuoteRequest = (state: IStoreState) =>
  state.premierCollectionBook.priceQuoteRequest;

// Corporate Travel
export const getTripPurpose = (state: IStoreState) =>
  state.premierCollectionBook.tripPurpose;

export const getTripDescription = (state: IStoreState) =>
  state.premierCollectionBook.tripDescription;

// For Hotel Ancillaries, the priceQuote is only the hotelQuote and doesn't include
// the ancillary costs. The ancillary costs are included in the combinedPricing that
// comes from the BE and is stored in the priceQuoteWithAncillaries. If
// priceQuoteWithAncillaries is available, we want to use that for the total amount due.
export const getPriceQuotePricing = createSelector(
  getPriceQuote,
  getPricingWithAncillaries,
  (priceQuote, pricingWithAncillaries) => {
    if (pricingWithAncillaries) {
      return pricingWithAncillaries;
    }
    return priceQuote?.pricing;
  },
);

export const getPriceQuoteErrors = (state: IStoreState) =>
  state.premierCollectionBook.priceQuoteErrors;

export const getSchedulePriceQuoteError = (state: IStoreState) =>
  state.premierCollectionBook.schedulePriceQuoteError;

export const getScheduleBookError = (state: IStoreState) =>
  state.premierCollectionBook.scheduleBookError;

export const getPollPriceQuoteCallState = (state: IStoreState) =>
  state.premierCollectionBook.pollPriceQuoteCallState;

export const getSchedulePriceQuoteCallState = (state: IStoreState) =>
  state.premierCollectionBook.schedulePriceQuoteCallState;

export const getConfirmationDetails = (state: IStoreState) =>
  state.premierCollectionBook.confirmationDetails;

export const getConfirmationDetailsError = (state: IStoreState) =>
  state.premierCollectionBook.confirmationDetailsErrors;

export const getConfirmationDetailsCallState = (state: IStoreState) =>
  state.premierCollectionBook.confirmationDetailsCallState;

export const getOffers = createSelector(
  (state: IStoreState) => state.premierCollectionBook.offers,
  (offers) => filterOutSpendAndGetOffers(offers ?? []),
);

export const getOfferToApply = (state: IStoreState) => {
  const offer = state.premierCollectionBook.offerToApply;
  // Don't return spend-and-get offers
  if (offer && isSpendAndGetOffer(offer.descriptions)) return undefined;
  return offer;
};

export const getCredit = createSelector(
  [(state: IStoreState) => state.premierCollectionBook.credit],
  (credit) => {
    if (credit) {
      return {
        ...credit,
        amount: {
          ...credit.amount,
          amount: truncateToTwoDecimals(credit.amount.amount),
        },
      } satisfies typeof credit;
    }
    return credit;
  },
);

export const getApplicableCredit = createSelector(
  getCredit,
  getPremierCollectionShopSelectedAvailability,
  getSearchedNightCount,
  (credit, selectedLodging, nightCount) => {
    if (!credit) return credit;
    return getCreditsAvailableByRestrictions(
      {
        product: RestrictedToProductsEnum.Lodging,
        collectionType: selectedLodging?.lodgingCollection as unknown as CreditCollectionTypeEnum,
        nights: nightCount ?? undefined,
      },
      credit
    );
  },
);

export const getApplicableCreditsBreakdown = createSelector(
  getCreditBreakdown,
  getPremierCollectionShopSelectedAvailability,
  getSearchedNightCount,
  (creditBreakdown, selectedLodging, nightCount) =>
    getCreditBreakdownAvailableByRestrictions(
      {
        product: RestrictedToProductsEnum.Lodging,
        collectionType: selectedLodging?.lodgingCollection as unknown as CreditCollectionTypeEnum,
        nights: nightCount ?? undefined,
      },
      creditBreakdown,
    ),
);

export const getIneligibleCreditsBreakdown = createSelector(
  getCreditBreakdown,
  getPremierCollectionShopSelectedAvailability,
  getSearchedNightCount,
  (creditBreakdown, selectedLodging, nightCount) =>
    getCreditBreakdownIneligibleByRestrictions(
      {
        product: RestrictedToProductsEnum.Lodging,
        collectionType: selectedLodging?.lodgingCollection as unknown as CreditCollectionTypeEnum,
        nights: nightCount ?? undefined,
      },
      creditBreakdown,
    ),
);

export const getCreditToApply = createSelector(
  [(state: IStoreState) => state.premierCollectionBook.creditToApply],
  (credit) => {
    if (credit) {
      return {
        ...credit,
        amount: {
          ...credit.amount,
          amount: truncateToTwoDecimals(credit.amount.amount),
        },
      } satisfies typeof credit;
    }
    return credit;
  },
);

const RESTRICTED_CREDIT_PRODUCT_TYPES: StatementCreditProductType[] = [
  StatementCreditProductType.Vesper,
];

export const getEligibleRestrictedPcCredits = createSelector(
  getCredit,
  getPremierCollectionShopSelectedAvailability,
  getSearchedNightCount,
  (credit, selectedLodging, nightCount): StatementCreditDetail[] => {
    if (!credit?.breakdown) return [];
    const context = {
      product: RestrictedToProductsEnum.Lodging,
      collectionType: selectedLodging?.lodgingCollection as unknown as CreditCollectionTypeEnum,
      nights: nightCount ?? undefined,
    };
    return credit.breakdown.filter(
      (breakdownEntry): breakdownEntry is StatementCreditDetail => {
        if (breakdownEntry.CreditDetail !== "Statement") return false;
        const creditDetail = breakdownEntry as StatementCreditDetail;
        if (!RESTRICTED_CREDIT_PRODUCT_TYPES.includes(creditDetail.productType)) return false;
        if (!creditDetail.restrictions?.length) return false;
        const hasPremierCollectionRestriction = creditDetail.restrictions.some(
          (restriction: CreditRestriction) =>
            restriction.RestrictionCriterion === "CollectionType" &&
            restriction.allowedTypes.includes(CreditCollectionTypeEnum.Premier),
        );
        if (!hasPremierCollectionRestriction) return false;
        return isCreditEligibleForRestrictions(creditDetail, context);
      },
    );
  },
);


export const getAppliedRestrictedCredits = createSelector(
  (state: IStoreState) => state.premierCollectionBook.appliedRestrictedCredits,
  (applied): AppliedRestrictedCredit[] => applied ?? [],
);

export const getTotalRestrictedCreditAmountApplied = createSelector(
  getAppliedRestrictedCredits,
  (applied): number =>
    applied.reduce((sum, c) => sum + c.amount, 0),
);

export const getRestrictedCreditAmountToApply = getTotalRestrictedCreditAmountApplied;

export const getTravelWalletItemsToApply = createSelector(
  getOfferToApply,
  getCreditToApply,
  (offerToApply, creditToApply) => ({
    walletItem: offerToApply || creditToApply,
    offerToApply,
    creditToApply,
  }),
);

export const getFetchApplicableTravelWalletItemsCallState = (
  state: IStoreState,
) => state.premierCollectionBook.fetchApplicableTravelWalletItemsCallState;

export const getBestOfferOverall = (state: IStoreState) =>
  state.premierCollectionBook.bestOfferOverall;

export const getProductPricingEstimate = createSelector(
  getPremierCollectionShopChosenProduct,
  (product) => product?.total || null,
);

export const getProductPricingEstimateWithAllFees = createSelector(
  getPremierCollectionShopChosenProduct,
  (product) => product?.tripTotal || null,
);

export const getPricingEstimateTotal = createSelector(
  getProductPricingEstimate,
  getAddedAncillariesPricing,
  (productEstimate, addedAncillaryPricing) =>
    productEstimate
      ? getSubtotalWithAncillariesLegacy(productEstimate, addedAncillaryPricing)
      : null,
);

export const getPricingEstimateTotalWithAllFees = createSelector(
  getProductPricingEstimateWithAllFees,
  getAddedAncillariesPricing,
  (productEstimate, addedAncillaryPricing) =>
    productEstimate
      ? getSubtotalWithAncillariesLegacy(productEstimate, addedAncillaryPricing)
      : null,
);

export const getPriceDifferenceAcknowledged = (state: IStoreState) =>
  state.premierCollectionBook.priceDifferenceAcknowledged;

export const getPriceDifference = createSelector(
  getPriceQuote,
  getPricingEstimateTotal,
  getPriceDifferenceAcknowledged,
  getPriceQuotePricing,
  getChosenRoomHasPayLater,
  (
    priceQuote,
    estimate,
    acknowledged,
    priceQuotePricing,
    hasPayLater,
  ): {
    hasDifference: boolean;
    isIncrease: boolean;
    predictedTotal: number;
    priceQuoteTotal: number;
    amount?: string;
    fiatAmount?: FiatPrice;
  } => {
    if (
      hasPayLater ||
      !priceQuote ||
      !priceQuotePricing ||
      !estimate ||
      acknowledged
    ) {
      return {
        hasDifference: false,
        isIncrease: false,
        predictedTotal: 0,
        priceQuoteTotal: 0,
      };
    }

    const predictedTotal = estimate.fiat.value;
    const priceQuoteTotal = priceQuotePricing?.payNowTotal.fiat.value;
    const delta = Math.abs(priceQuoteTotal - predictedTotal);

    return {
      // If the difference is within a dollar we don't show the error modal
      hasDifference: delta >= 1,
      isIncrease: priceQuoteTotal > predictedTotal,
      amount: delta.toFixed(0).toString(),
      // Delta as a FiatPrice so copy formats it in the quote's currency, not a
      // hardcoded symbol (CT1261T-420/421).
      fiatAmount: {
        value: delta,
        currencySymbol: priceQuotePricing.payNowTotal.fiat.currencySymbol,
        currencyCode: priceQuotePricing.payNowTotal.fiat.currencyCode,
      },
      predictedTotal,
      priceQuoteTotal,
    };
  },
);

// CT1261T-421: a price difference keeps the legacy blocking modal unless it's a
// flag-on consumer decrease, which is shown inline instead (corp has no inline-
// message UI yet). Shared by every price-difference gate so they can't drift.
const isPriceDifferenceBlocking = (
  priceDifference: ReturnType<typeof getPriceDifference>,
  isLodgingErrorCopyUpdateEnabled: boolean
): boolean =>
  priceDifference.isIncrease ||
  !isLodgingErrorCopyUpdateEnabled ||
  isCorpTenant(config.TENANT);

export const getRewardsConversionFailed = (state: IStoreState) =>
  state.premierCollectionBook.rewardsConversionFailed;

export const getHasError = createSelector(
  getUserPassengerCallState,
  getPriceDifference,
  getPriceDifferenceAcknowledged,
  getPollPriceQuoteCallState,
  getScheduleBookError,
  getSchedulePriceQuoteError,
  getConfirmationDetailsCallState,
  getRewardsConversionFailed,
  getVerifyPaymentMethodCallState,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    passengerCallState,
    priceDifference,
    priceDifferenceAcknowledged,
    priceQuoteCallState,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsCallState,
    rewardsConversionFailed,
    verifyPaymentMethodCallState,
    isLodgingErrorCopyUpdateEnabled,
  ) =>
    passengerCallState === CallState.Failed ||
    // CT1261T-421: a non-blocking decrease isn't an error.
    (priceDifference.hasDifference &&
      !priceDifferenceAcknowledged &&
      isPriceDifferenceBlocking(
        priceDifference,
        isLodgingErrorCopyUpdateEnabled,
      )) ||
    !!rewardsConversionFailed ||
    !!scheduleBookError ||
    !!schedulePriceQuoteError ||
    verifyPaymentMethodCallState === CallState.Failed ||
    confirmationDetailsCallState === CallState.Failed ||
    priceQuoteCallState === CallState.Failed,
);

// The code sits directly on `ErrorCode`, or nested in a `ProductError`'s opaque
// value as `LodgingError` (the BFF's quote/book error shape).
const getHotelBookErrorCode = (error: PurchaseError): string | undefined => {
  switch (error.Error) {
    case PurchaseErrorEnum.ErrorCode:
      return (error as ErrorCode).code;
    case PurchaseErrorEnum.ProductError: {
      const productError = error as ProductError;
      return (productError.value.value?.LodgingError ||
        productError.value.value) as string;
    }
    default:
      return undefined;
  }
};

// Error copy + a connection flag that getIsConnectionError reads back, so the
// classification isn't re-derived (CT1261T-419).
type ResolvedErrorTitles = ErrorTitles & { isConnection?: boolean };

const getErrorMessage = ({
  error,
  isLodgingErrorCopyUpdateEnabled = false,
}: {
  error: PurchaseError;
  isLodgingErrorCopyUpdateEnabled?: boolean;
}): ResolvedErrorTitles => {
  const code = getHotelBookErrorCode(error) ?? "";
  let agentSubtitle = getAgentErrorSubtitle(error.Error);
  let agentTitle = getAgentErrorTitle(error.Error);
  // Unmapped/unknown codes resolve here: FALLBACK (CT1261T-422) when the flag is
  // on, legacy GENERIC when off. Matched cases below overwrite this.
  let titles: ErrorTitles = isLodgingErrorCopyUpdateEnabled
    ? textConstants.FALLBACK_ERROR_TITLES
    : textConstants.GENERIC_ERROR_TITLES;
  // Flags connection/quote-expired codes; read back via getIsConnectionError.
  let isConnection = false;

  const handleNoAvailability = (
    noAvailabilityCode:
      | HotelBookErrorType.LackOfInventory
      | HotelBookErrorType.RateNotAvailable
      | HotelBookErrorType.RateSoldOut
      | HotelBookErrorType.RoomSoldOut
      | HotelBookErrorType.RateChanged,
  ) => {
    if (isLodgingErrorCopyUpdateEnabled) {
      titles = textConstants.UNAVAILABLE_OPTION_TITLES;
    } else {
      switch (noAvailabilityCode) {
        case HotelBookErrorType.LackOfInventory:
        case HotelBookErrorType.RateSoldOut:
        case HotelBookErrorType.RoomSoldOut:
          titles = textConstants.LACK_OF_INVENTORY_TITLES;
          break;
        case HotelBookErrorType.RateNotAvailable:
        case HotelBookErrorType.RateChanged:
          titles = textConstants.RATE_NOT_AVAILABLE_TITLES;
          break;
      }
    }

    trackEvent({
      eventName: NO_AVAILABILITY_HOTELS,
      properties: {},
    });
  };

  switch (error.Error) {
    case PurchaseErrorEnum.PaymentError:
      const paymentError = error as PaymentError;
      if (paymentError.value.type === Payment.Rewards)
        titles = textConstants.REDEMPTION_FAILED_TITLES;
      // Check for CardDeclined with custom backend messages
      if (paymentError.value.type === Payment.Card) {
        const cardDeclined = paymentError.value.value;
        if (cardDeclined.messageTitle && cardDeclined.messageBody) {
          titles = {
            icon: textConstants.ERROR_ICON,
            title: cardDeclined.messageTitle,
            subtitle: cardDeclined.messageBody,
            primaryButtonText: textConstants.EDIT_PAYMENT_INFO,
          };
        }
      }
      break;
    // ErrorCode and ProductError envelopes resolve to the same `code` space and
    // identical copy, so they share one body (QuoteExpired → CT1261T-419 lives
    // here once, not per-envelope).
    case PaymentErrorEnum.ErrorCode:
    case PurchaseErrorEnum.ProductError:
      agentSubtitle = getAgentErrorSubtitle(code);
      agentTitle = getAgentErrorTitle(code);

      switch (code) {
        case PaymentErrorEnum.UserCardNotFound:
          titles = textConstants.USER_CARD_ERROR_TITLES;
          break;
        case PaymentErrorEnum.FraudAutoReject:
        case PaymentErrorEnum.LikelyFraud:
          titles = textConstants.FRAUD_TITLES;
          break;
        case HotelBookErrorType.CheckInMinimumAgeNotMet:
          titles = textConstants.CHECK_IN_AGE_NOT_MET;
          break;
        case HotelBookErrorType.PaymentError:
        case HotelBookErrorType.ProviderPaymentError:
          titles = textConstants.PAYMENT_ERROR_TITLES;
          break;
        case HotelBookErrorType.LackOfInventory:
        case HotelBookErrorType.RateNotAvailable:
        case HotelBookErrorType.RateSoldOut:
        case HotelBookErrorType.RoomSoldOut:
        case HotelBookErrorType.RateChanged:
          handleNoAvailability(code);
          break;
        case LodgingErrorEnum.QuoteExpired:
          // CT1261T-419: retryable connection/quote-expired modal (flag-gated).
          if (isLodgingErrorCopyUpdateEnabled) {
            titles = textConstants.CONNECTION_ERROR_TITLES;
            isConnection = true;
          }
          break;
        case PaymentErrorEnum.RedemptionFailure:
          titles = textConstants.REDEMPTION_FAILED_TITLES;
          break;
      }
      break;
    default:
  }

  return { ...titles, agentSubtitle, agentTitle, isConnection };
};

// Mirrors the codes routed to LACK_OF_INVENTORY_TITLES /
// RATE_NOT_AVAILABLE_TITLES in handleNoAvailability above.
const UNAVAILABLE_HOTEL_BOOK_ERROR_CODES: string[] = [
  HotelBookErrorType.LackOfInventory,
  HotelBookErrorType.RateSoldOut,
  HotelBookErrorType.RoomSoldOut,
  HotelBookErrorType.RateNotAvailable,
  HotelBookErrorType.RateChanged,
];

// Story 1: confirmation-details errors take priority over price-quote errors,
// matching getErrorTitles.
export const getIsHotelUnavailableError = createSelector(
  getHasError,
  getConfirmationDetailsError,
  getPriceQuoteErrors,
  (hasError, confirmationDetailsErrors, priceQuoteErrors): boolean => {
    if (!hasError) return false;

    const error = confirmationDetailsErrors[0] ?? priceQuoteErrors[0];
    if (error == null) return false;

    const code = getHotelBookErrorCode(error);
    return code != null && UNAVAILABLE_HOTEL_BOOK_ERROR_CODES.includes(code);
  },
);

export const getScheduleBookCallState = (state: IStoreState) =>
  state.premierCollectionBook.scheduleBookCallState;

export const priceQuoteParamsSelector = createSelector(
  getFromDate,
  getUntilDate,
  getPremierCollectionShopChosenRoomInfo,
  getPremierCollectionShopChosenProduct,
  getUserSelectedTravelerId,
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getPremierCollectionShopSelectedAvailability,
  getAgentEmail,
  selectedCfarIdSelector,
  (
    fromDate,
    untilDate,
    roomInfo,
    product,
    personId,
    emailAddress,
    phoneNumber,
    lodging,
    agentEmail,
    selectedCfarId,
  ): HotelPriceQuoteScheduleRequestV2 | null => {
    if (!product || !personId || !lodging) {
      return null;
    }
    const hotelQuoteRequest: HotelQuoteScheduleRequest = {
      token: "00000000-0000-0000-0000-000000000000",
      quoteRequest: product?.opaqueQuoteRequest,
      personId,
      emailAddress: emailAddress || "",
      phoneNumber: phoneNumber || "",
      dates:
        fromDate && untilDate
          ? {
            from: dayjs(fromDate).format("YYYY-MM-DD"),
            until: dayjs(untilDate).format("YYYY-MM-DD"),
          }
          : undefined,
      id: lodging.lodging.id,
      name: lodging.lodging.name,
      city: lodging.lodging.city,
      country: lodging.lodging.country,
      starRating: lodging.lodging.starRating,
      providerName: lodging.price?.providerName,
      bedTypes: roomInfo?.roomInfo.beds,
      lodgingCollection: lodging.lodgingCollection
        ? lodging.lodgingCollection
        : LodgingCollectionEnum.NoCollection,
    };
    if (agentEmail) {
      hotelQuoteRequest.delegatedTo = agentEmail;
    }
    return {
      hotelQuoteRequest,
      hotelAncillaryIds: {
        cfarId:
          selectedCfarId && selectedCfarId?.value !== DO_NOT_APPLY_OPTION_KEY
            ? selectedCfarId
            : undefined,
      },
    };
  },
);

export const bookingInProgressSelector = createSelector(
  getScheduleBookCallState,
  getConfirmationDetailsCallState,
  (scheduleBookCallState, confirmationCallState) =>
    scheduleBookCallState === CallState.InProcess ||
    confirmationCallState === CallState.InProcess,
);

export const hasNoUserPassengersSelector = createSelector(
  getUserPassengers,
  getUserPassengerCallState,
  (userPassengers, userPassengerCallState) =>
    userPassengers.length === 0 && userPassengerCallState === CallState.Success,
);

// PAYMENT

export const getRewardsPaymentAccountReferenceId = (state: IStoreState) =>
  state.premierCollectionBook.rewardsAccountReferenceId;

export const getRewardsPaymentAccount = createSelector(
  getRewardsPaymentAccountReferenceId,
  getRewardsAccounts,
  (accountId, rewardsAccounts) =>
    rewardsAccounts.find((account) => account.accountReferenceId === accountId),
);

export const getRewardsPaymentInFiatCurrency = (state: IStoreState) =>
  state.premierCollectionBook.rewardsPaymentInFiatCurrency;

export const getRewardsPaymentInRewardsCurrency = (state: IStoreState) =>
  state.premierCollectionBook.rewardsPaymentTotal;

export const getRewardsRemainingAfterPurchase = createSelector(
  getRewardsPaymentAccount,
  getRewardsPaymentInFiatCurrency,
  (paymentAccount, rewardsPaymentAmount) => {
    if (!paymentAccount) return "";

    const {
      rewardsCashEquivalent: { value, currencyCode, currencySymbol },
    } = paymentAccount;

    const remainder = value - (rewardsPaymentAmount?.value || 0);

    return getTotalPriceText({
      price: {
        currencyCode,
        currencySymbol,
        value: remainder,
      },
      priceFormatter: twoDecimalFormatter,
    });
  },
);

export const getTravelWalletOfferToApplyAmount = createSelector(
  getTravelWalletItemsToApply,
  getProductPricingEstimateWithAllFees,
  getPriceQuotePricing,
  (travelWalletItemsToApply) => {
    if (travelWalletItemsToApply.offerToApply) {
      return travelWalletItemsToApply.offerToApply.amount.amount * -1;
    }
    return 0;
  },
);

export const getTravelWalletCreditToApplyAmount = createSelector(
  getTravelWalletItemsToApply,
  getPriceQuotePricing,
  getPremierCollectionShopChosenProduct,
  getTravelWalletOfferToApplyAmount,
  getRestrictedCreditAmountToApply,
  (
    travelWalletItemsToApply,
    priceQuotePricing,
    product,
    offerAppliedAmount,
    restrictedCreditAmount,
  ) => {
    const totalFiatValue =
      priceQuotePricing?.payNowTotal.fiat.value ||
      product?.total.fiat.value ||
      0;
    const restrictedApplied = Math.abs(restrictedCreditAmount ?? 0);
    const totalFiatValueWithOfferApplied: number =
      totalFiatValue - offerAppliedAmount - restrictedApplied;
    if (
      travelWalletItemsToApply.creditToApply &&
      totalFiatValueWithOfferApplied > 0
    ) {
      const maxApplicableCredit =
        travelWalletItemsToApply.offerToApply?.maxApplicableCredit?.amount;
      const amount =
        maxApplicableCredit &&
          maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount
          ? maxApplicableCredit
          : travelWalletItemsToApply.creditToApply.amount.amount;
      if (totalFiatValueWithOfferApplied < amount * -1) {
        if (
          maxApplicableCredit &&
          maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount &&
          travelWalletItemsToApply.creditToApply.amount.amount * -1 <
          totalFiatValueWithOfferApplied
        ) {
          return travelWalletItemsToApply.creditToApply.amount.amount * -1;
        }
        return totalFiatValueWithOfferApplied;
      }
      if (
        maxApplicableCredit &&
        maxApplicableCredit <
        travelWalletItemsToApply.creditToApply.amount.amount
      ) {
        return travelWalletItemsToApply.creditToApply.amount.amount * -1;
      }
      return amount * -1;
    }
    return 0;
  },
);

export const getMaxApplicableRestrictedCreditAmounts = createSelector(
  getPriceQuotePricing,
  getEligibleRestrictedPcCredits,
  getTravelWalletOfferToApplyAmount,
  getRewardsPaymentInFiatCurrency,
  getAppliedRestrictedCredits,
  getCreditToApply,
  (priceQuotePricing, eligibleCredits, offerAmount, rewardsFiat, appliedCredits, regularCreditToApply): AppliedRestrictedCredit[] => {
    // Only compute against the finalized price quote
    const bookingTotal = priceQuotePricing?.payNowTotal.fiat.value ?? 0;
    const rewardsAmount = Math.abs(rewardsFiat?.value ?? 0);
    const offerAmountAbs = Math.abs(offerAmount);
    const regularCreditCommittedAbs = Math.abs(regularCreditToApply?.amount.amount ?? 0);

    // How much of the booking is still unpaid after offers, rewards, and
    // whatever the regular travel credit already committed. Each restricted
    // credit can only apply against this remaining balance.
    let remainingBalance = bookingTotal - offerAmountAbs - rewardsAmount - regularCreditCommittedAbs;

    return eligibleCredits.map((eligibleCredit) => {
      const creditId = eligibleCredit.capOneCreditId.value;
      const usableAmount = Math.abs(eligibleCredit.usableAmount.amount);

      if (remainingBalance <= 0) {
        return { capOneCreditId: creditId, amount: 0 };
      }

      // The ceiling the user is allowed to apply for this credit - bounded by
      // their usable balance and the booking balance still owed before this
      // credit. This is independent of how much they've currently applied.
      const ceiling = Math.min(remainingBalance, usableAmount);

      // Decrement the running balance by what is actually applied (a
      // user-selected amount clamped to the ceiling, otherwise the full
      // ceiling) so any subsequent credit sees the correct remainder.
      const userSelectedEntry = appliedCredits.find((appliedCredit) => appliedCredit.capOneCreditId === creditId);
      const userSelectedAmount = userSelectedEntry ? Math.abs(userSelectedEntry.amount) : null;
      const appliedAmount = userSelectedAmount !== null
        ? Math.min(userSelectedAmount, ceiling)
        : ceiling;

      remainingBalance -= appliedAmount;

      // Amounts are stored as negative to match the wallet credit convention
      return { capOneCreditId: creditId, amount: ceiling * -1 };
    });
  },
);


export const getIsRestrictedCreditCoveringTotal = createSelector(
  getPriceQuotePricing,
  getPremierCollectionShopChosenProduct,
  getTravelWalletOfferToApplyAmount,
  getRewardsPaymentInFiatCurrency,
  getTotalRestrictedCreditAmountApplied,
  (priceQuotePricing, product, offerAmount, rewardsFiat, restrictedCreditAmount) => {
    const restrictedApplied = Math.abs(restrictedCreditAmount ?? 0);
    if (restrictedApplied === 0) return false;
    const total =
      priceQuotePricing?.payNowTotal.fiat.value ||
      product?.total.fiat.value ||
      0;
    const rewardsAmount = rewardsFiat?.value ?? 0;
    const remaining = total - Math.abs(offerAmount) - Math.abs(rewardsAmount);
    return remaining > 0 && restrictedApplied >= remaining;
  },
);

export const getMaxApplicableTravelWalletCreditOnlyAmount = createSelector(
  getApplicableCredit,
  getPremierCollectionShopChosenProduct,
  getPriceQuotePricing,
  (credit, product, priceQuotePricing) => {
    if (priceQuotePricing && credit) {
      if (
        priceQuotePricing.payNowTotal.fiat.value <
        credit.amount.amount * -1
      ) {
        return priceQuotePricing.payNowTotal.fiat.value;
      }
      return credit.amount.amount * -1;
    }
    if (product && credit) {
      const totalFiatValue = product.total.fiat.value + credit?.amount.amount;
      if (totalFiatValue < 0) {
        return product.total.fiat.value * -1;
      }

      return credit.amount.amount * -1;
    }
    return 0;
  },
);

export const getMaxApplicableTravelWalletCreditAmount = createSelector(
  getApplicableCredit,
  getPremierCollectionShopChosenProduct,
  getPriceQuotePricing,
  getTravelWalletOfferToApplyAmount,
  getRestrictedCreditAmountToApply,
  (credit, product, priceQuotePricing, offerAppliedAmount, restrictedCreditAmount) => {
    const restrictedApplied = Math.abs(restrictedCreditAmount ?? 0);
    if (priceQuotePricing && credit) {
      const remaining =
        priceQuotePricing.payNowTotal.fiat.value - offerAppliedAmount - restrictedApplied;
      if (remaining < credit.amount.amount * -1) {
        return remaining * -1;
      }
      return credit.amount.amount;
    }
    if (product && credit) {
      const totalFiatValue = product.total.fiat.value + credit.amount.amount - restrictedApplied;
      if (totalFiatValue < 0) {
        return (product.total.fiat.value - offerAppliedAmount - restrictedApplied) * -1;
      }
      return credit.amount.amount;
    }
    return 0;
  },
);

export const getTotalToPay = createSelector(
  getPricingEstimateTotal,
  getTravelWalletItemsToApply,
  getPriceQuotePricing,
  getRestrictedCreditAmountToApply,
  (estimate, travelWalletItemsToApply, priceQuotePricing, restrictedCreditAmount): Prices | null => {
    const total = priceQuotePricing ? priceQuotePricing.payNowTotal : estimate;
    const restrictedAmount = restrictedCreditAmount ?? 0;

    const rewardsTotal: Record<string, RewardsPrice> = {};
    if (
      total &&
      (travelWalletItemsToApply.offerToApply ||
        travelWalletItemsToApply.creditToApply ||
        Math.abs(restrictedAmount) > 0)
    ) {
      let walletItemsTotal: number =
        travelWalletItemsToApply.offerToApply?.amount.amount || 0;

      if (travelWalletItemsToApply.creditToApply) {
        const maxApplicableCredit =
          travelWalletItemsToApply.offerToApply?.maxApplicableCredit?.amount;
        if (
          maxApplicableCredit &&
          maxApplicableCredit >
          travelWalletItemsToApply.creditToApply.amount.amount
        ) {
          walletItemsTotal += maxApplicableCredit;
        } else {
          walletItemsTotal +=
            travelWalletItemsToApply.creditToApply.amount.amount;
        }
      }

      // Add restricted credit (stored as negative) to the wallet total
      walletItemsTotal += restrictedAmount;

      Object.keys(total.rewards).forEach((accountId) => {
        if (total) {
          let rewardTotalValue = total.rewards[accountId].value;
          rewardTotalValue =
            total.rewards[accountId].currency === "Cash"
              ? rewardTotalValue + walletItemsTotal
              : rewardTotalValue + walletItemsTotal * 100;
          rewardsTotal[accountId] = {
            currency: total.rewards[accountId].currency,
            currencyDescription: total.rewards[accountId].currencyDescription,
            value: rewardTotalValue > 0 ? rewardTotalValue : 0,
          };
        }
      });
      return {
        fiat: {
          currencyCode: total.fiat.currencyCode,
          currencySymbol: total.fiat.currencySymbol,
          value:
            total.fiat.value + walletItemsTotal > 0
              ? total.fiat.value + walletItemsTotal
              : 0,
        },
        rewards: rewardsTotal,
      };
    }
    return priceQuotePricing ? priceQuotePricing.payNowTotal : estimate;
  },
);

export const getIsBookingTotalCovered = createSelector(
  getTotalToPay,
  (total) => total?.fiat.value === 0,
);

export const getIsTravelWalletCreditPaymentOnly = createSelector(
  getTotalToPay,
  getTravelWalletItemsToApply,
  getRestrictedCreditAmountToApply,
  (total, travelWalletItemsToApply, restrictedCreditAmount) => {
    const regularCreditApplied =
      !travelWalletItemsToApply.offerToApply &&
      travelWalletItemsToApply.creditToApply;
    const restrictedApplied =
      !travelWalletItemsToApply.offerToApply &&
      restrictedCreditAmount && Math.abs(restrictedCreditAmount) > 0;
    if ((regularCreditApplied || restrictedApplied) && total?.fiat.value === 0) {
      return true;
    }
    return false;
  },
);

export const getIsTravelWalletOfferPaymentOnly = createSelector(
  getTotalToPay,
  getTravelWalletItemsToApply,
  getRestrictedCreditAmountToApply,
  (total, travelWalletItemsToApply, restrictedCreditAmount) => {
    const restrictedApplied =
      restrictedCreditAmount && Math.abs(restrictedCreditAmount) > 0;
    if (
      travelWalletItemsToApply.offerToApply &&
      !travelWalletItemsToApply.creditToApply &&
      !restrictedApplied &&
      total?.fiat.value === 0
    ) {
      return true;
    }
    return false;
  },
);

export const getIsStackedTravelWalletPaymentOnly = createSelector(
  getTotalToPay,
  getTravelWalletItemsToApply,
  getRestrictedCreditAmountToApply,
  (total, travelWalletItemsToApply, restrictedCreditAmount) => {
    const restrictedApplied =
      restrictedCreditAmount && Math.abs(restrictedCreditAmount) > 0;
    if (
      travelWalletItemsToApply.offerToApply &&
      (travelWalletItemsToApply.creditToApply || restrictedApplied) &&
      total?.fiat.value === 0
    ) {
      return true;
    }
    return false;
  },
);

export const getIsTravelWalletPaymentOnly = createSelector(
  getIsTravelWalletCreditPaymentOnly,
  getIsTravelWalletOfferPaymentOnly,
  getIsStackedTravelWalletPaymentOnly,
  (
    isTravelWalletCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly,
  ) => {
    if (
      isTravelWalletCreditPaymentOnly ||
      isTravelWalletOfferPaymentOnly ||
      isStackedTravelWalletPaymentOnly
    ) {
      return true;
    }
    return false;
  },
);

export const getIsCreditCardPaymentRequired = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  getIsTravelWalletPaymentOnly,
  (total, rewardsPaymentAmount, isTravelWalletPaymentOnly) => {
    if (isTravelWalletPaymentOnly) return false;

    if (!total) return true;
    if (!rewardsPaymentAmount) return true;

    return total.fiat.value > rewardsPaymentAmount.value;
  },
);

export const getTotalCreditCardPaymentRequired = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  (estimate, rewardsPaymentAmount) => {
    if (!estimate) return "";

    if (!rewardsPaymentAmount) {
      return getTotalPriceText({
        price: estimate.fiat,
        priceFormatter: twoDecimalFormatter,
      });
    }

    const remainder = estimate.fiat.value - rewardsPaymentAmount.value;
    const total = remainder <= 0 ? 0 : remainder;

    return getTotalPriceText({
      price: {
        currencyCode: estimate.fiat.currencyCode,
        currencySymbol: estimate.fiat.currencySymbol,
        value: total,
      },
      priceFormatter: twoDecimalFormatter,
    });
  },
);

export const getTotalCreditCardPaymentRequiredNumber = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  (estimate, rewardsPaymentAmount) => {
    if (!estimate) return null;

    if (!rewardsPaymentAmount) {
      return estimate.fiat.value;
    }

    const remainder = estimate.fiat.value - rewardsPaymentAmount.value;
    const total =
      remainder <= 0 ? 0 : Math.round((remainder + Number.EPSILON) * 100) / 100;
    return total;
  },
);

export const getTotalCreditCardPaymentRequiredInFiatPrice = createSelector(
  getTotalToPay,
  getRewardsPaymentInFiatCurrency,
  (totalToPay, rewardsPaymentAmount): FiatPrice | undefined => {
    if (!totalToPay) return undefined;

    if (!rewardsPaymentAmount) return totalToPay.fiat;

    const remainder = totalToPay.fiat.value - rewardsPaymentAmount.value;
    const total = remainder <= 0 ? 0 : remainder;

    return {
      currencyCode: totalToPay.fiat.currencyCode,
      currencySymbol: totalToPay.fiat.currencySymbol,
      value: total,
    };
  },
);

export const getPaymentMethodRewardsAccountId = (state: IStoreState) =>
  state.premierCollectionBook.paymentMethodRewardsAccountId;

export const getAddedAncillaryLineItems = createSelector(
  getAddedAncillariesPricing,
  (addedAncillariesPricing): IHotelPriceLineItem[] =>
    // CFAR - The Prices should be taken from priceQuote once BE work is done
    addedAncillariesPricing
      .map((pricing) => {
        switch (pricing.kind) {
          case AncillaryKindEnum.Cfar:
            return {
              icon: "check-shield-blue",
              title: textConstants.CANCEL_FOR_ANY_REASON,
              value: getTotalPriceText({
                price: pricing.premium.fiat,
                priceFormatter: twoDecimalFormatter,
              }),
            };
          default:
            return [];
        }
      })
      .flat(),
);

export const getPremierCollectionPricingLineItems = createSelector(
  getSearchedNightCount,
  getPremierCollectionShopChosenProduct,
  getPriceQuote,
  getRewardsPaymentAccount,
  getPaymentMethodRewardsAccountId,
  getRewardsAccounts,
  getPriceQuotePricing,
  getAddedAncillaryLineItems,
  getAddedAncillariesPricing,
  getIsChosenRoomPayLater,
  (
    nights,
    product,
    priceQuote,
    rewardsPaymentAccount,
    paymentMethodRewardsAccountId,
    rewardsAccounts,
    priceQuotePricing,
    addedAncillaryLineItems,
    addedAncillaryPricing,
    isPayLater,
  ): IHotelPriceLineItem[][] => {
    if (priceQuote && priceQuotePricing && product) {
      const rewardsAccountId = rewardsPaymentAccount
        ? rewardsPaymentAccount.accountReferenceId
        : paymentMethodRewardsAccountId ||
        Object.keys(product.total.rewards)[0];
      const total = priceQuotePricing.payNowTotal;

      const includeRewards =
        !isPayLater &&
        (rewardsAccounts.find(
          (account) => account.accountReferenceId === rewardsAccountId,
        )?.allowRewardsRedemption ??
          true);

      const dueTodayPriceLine = {
        title: textConstants.DUE_TODAY,

        value: getTotalPriceText({
          price: {
            currencyCode: total.fiat.currencyCode,
            currencySymbol: total.fiat.currencySymbol,
            value: total.fiat.value,
          },
          priceFormatter: twoDecimalFormatter,
        }),
        rewardsValue: includeRewards
          ? getRewardsString(total.rewards[rewardsAccountId])
          : undefined,
        boldLabel: true,
        type: "due-today",
      } as IHotelPriceLineItem;
      return [
        [
          {
            title: textConstants.ROOM_TOTAL(nights, 1),
            value: getTotalPriceText({
              price: priceQuotePricing.subtotal.fiat,
              priceFormatter: twoDecimalFormatter,
            }),
          } as IHotelPriceLineItem,
          {
            title: textConstants.TAXES_AND_FEES,
            value: getTotalPriceText({
              price: priceQuotePricing.taxes.fiat,
              priceFormatter: twoDecimalFormatter,
            }),
          } as IHotelPriceLineItem,
          ...addedAncillaryLineItems,
        ],
        [
          ...(!isPayLater ? [dueTodayPriceLine] : []),
          ...(priceQuotePricing.feeBreakdown.total
            ? [
              {
                title: isPayLater
                  ? textConstants.ADDITIONAL_FEES
                  : textConstants.ADDITIONAL_FEES_DUE_AT_CHECKIN,
                value: getTotalPriceText({
                  price: priceQuotePricing.feeBreakdown.total,
                  priceFormatter: twoDecimalFormatter,
                }),
                boldValue: false,
                type: "additional-fees",
              } as IHotelPriceLineItem,
            ]
            : []),
          ...(isPayLater ? [dueTodayPriceLine] : []),
          ...(isPayLater || priceQuotePricing.feeBreakdown.total
            ? [
              {
                title: isPayLater
                  ? textConstants.DUE_AT_HOTEL
                  : textConstants.TOTAL,
                value: getTotalPriceText({
                  price: priceQuotePricing.tripTotal.fiat,
                  priceFormatter: twoDecimalFormatter,
                }),
                boldLabel: true,
                type: "total",
                boldValue: true,
              } as IHotelPriceLineItem,
            ]
            : []),
        ],
      ];
    }
    if (product) {
      const rewardsAccount = rewardsPaymentAccount
        ? rewardsPaymentAccount.accountReferenceId
        : Object.keys(product.total.rewards)[0];

      const includeRewards =
        !isPayLater &&
        (rewardsAccounts.find(
          (account) => account.accountReferenceId === rewardsAccount,
        )?.allowRewardsRedemption ??
          true);

      const dueTodayTotal = getSubtotalWithAncillariesLegacy(
        product.total,
        addedAncillaryPricing,
      );

      const total = getSubtotalWithAncillariesLegacy(
        product.tripTotal,
        addedAncillaryPricing,
      );

      const dueTodayPriceLine = {
        title: textConstants.DUE_TODAY,
        value: getTotalPriceText({
          price: dueTodayTotal.fiat,
          priceFormatter: twoDecimalFormatter,
        }),
        rewardsValue: includeRewards
          ? getRewardsString(dueTodayTotal.rewards[rewardsAccount])
          : undefined,
        boldLabel: true,
        type: "due-today",
      } as IHotelPriceLineItem;

      return [
        [
          {
            title: textConstants.ROOM_TOTAL(nights, 1),
            value: getTotalPriceText({
              price: product.sellRate.fiat,
              priceFormatter: twoDecimalFormatter,
            }),
          } as IHotelPriceLineItem,
          {
            title: textConstants.TAXES_AND_FEES,
            value: getTotalPriceText({
              price: {
                value: Object.keys(product.taxBreakdown).reduce(
                  (sum, key) => product.taxBreakdown[key].fiat.value + sum,
                  0,
                ),
                currencyCode: product.sellRate.fiat.currencyCode,
                currencySymbol: product.sellRate.fiat.currencySymbol,
              },
              priceFormatter: twoDecimalFormatter,
            }),
          } as IHotelPriceLineItem,
          ...addedAncillaryLineItems,
        ],
        [
          ...(!isPayLater ? [dueTodayPriceLine] : []),
          ...(product.feeBreakdown.total
            ? [
              {
                title: isPayLater
                  ? textConstants.ADDITIONAL_FEES
                  : textConstants.ADDITIONAL_FEES_DUE_AT_CHECKIN,
                value: getTotalPriceText({
                  price: product.feeBreakdown.total,
                  priceFormatter: twoDecimalFormatter,
                }),
                boldValue: false,
                type: "additional-fees",
              } as IHotelPriceLineItem,
            ]
            : []),
          ...(isPayLater ? [dueTodayPriceLine] : []),
          ...(isPayLater || product.feeBreakdown.total
            ? [
              {
                title: isPayLater
                  ? textConstants.DUE_AT_HOTEL
                  : textConstants.TOTAL,
                value: getTotalPriceText({
                  price: total.fiat,
                  priceFormatter: twoDecimalFormatter,
                }),
                boldLabel: true,
                type: "total",
                boldValue: true,
              } as IHotelPriceLineItem,
            ]
            : []),
        ],
      ];
    }
    return [];
  },
);

export const getPremierCollectionTripTotalInPrices = createSelector(
  getPriceQuote,
  getPremierCollectionShopChosenProduct,
  getRewardsPaymentAccount,
  getRewardsAccountWithLargestValue,
  getPriceQuotePricing,
  getIsChosenRoomPayLater,
  (
    priceQuote,
    chosenProduct,
    rewardsPaymentAccount,
    rewardsAccountWithLargestValue,
    priceQuotePricing,
    isPayLater,
  ): { fiat: FiatPrice; rewards?: RewardsPrice } | null => {
    const activeRewardsAccount =
      rewardsPaymentAccount ?? rewardsAccountWithLargestValue;

    const includeRewards = isPayLater
      ? activeRewardsAccount?.allowRewardsRedemption ?? true
      : false;

    if (priceQuote && priceQuotePricing && activeRewardsAccount) {
      return {
        fiat: priceQuotePricing.tripTotal.fiat,
        rewards: includeRewards
          ? priceQuotePricing.tripTotal.rewards[
          activeRewardsAccount.accountReferenceId
          ]
          : undefined,
      };
    }
    if (chosenProduct && activeRewardsAccount) {
      return {
        fiat: chosenProduct.tripTotal.fiat,
        rewards: includeRewards
          ? chosenProduct.tripTotal.rewards[
          activeRewardsAccount.accountReferenceId
          ]
          : undefined,
      };
    }
    return null;
  },
);

export const getPremierCollectionRewardsAndTotalLineItems = createSelector(
  getRewardsPaymentAccount,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getTotalCreditCardPaymentRequired,
  getSelectedPaymentMethodId,
  getPaymentMethods,
  getTravelWalletItemsToApply,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletCreditToApplyAmount,
  getIsChosenRoomPayLater,
  getEligibleRestrictedPcCredits,
  getAppliedRestrictedCredits,
  (
    rewardsPaymentAccount,
    rewardsPayment,
    rewardsInFiat,
    totalAmountDueOnCredit,
    paymentMethodId,
    paymentMethods,
    travelWalletItemsToApply,
    offerAmountToApply,
    creditAmountToApply,
    isPayLater,
    eligibleRestrictedCredits,
    appliedRestrictedCredits,
  ) => {
    const rewardsLineItems = [];

    if (
      rewardsPaymentAccount &&
      (rewardsPaymentAccount.allowRewardsRedemption ?? true)
    ) {
      const fiatPrice = rewardsInFiat ?? {
        currencyCode: rewardsPaymentAccount?.rewardsCashEquivalent.currencyCode,
        currencySymbol:
          rewardsPaymentAccount?.rewardsCashEquivalent.currencySymbol,
        value: 0,
      };

      const priceText = getTotalPriceText({
        price: {
          ...fiatPrice,
          value: fiatPrice.value,
        },
        priceFormatter: twoDecimalFormatter,
      });

      const rewardsText = rewardsPayment
        ? `- ${getRewardsString(rewardsPayment)}`
        : "";
      rewardsLineItems.push({
        title: `${rewardsPaymentAccount?.productDisplayName} ${textConstants.REWARDS}`,
        value: `- ${priceText}`,
        rewardsValue: rewardsText,
      });
    }

    const paymentMethod = paymentMethods.find((p) => p.id === paymentMethodId);

    const amountDuePriceLine = {
      title: paymentMethod
        ? `Ending in ${paymentMethod.last4}:`
        : textConstants.AMOUNT_DUE,
      value: totalAmountDueOnCredit,
      icon: IconName.Payment,
    };

    if (
      (!isPayLater && travelWalletItemsToApply.offerToApply) ||
      travelWalletItemsToApply.creditToApply ||
      (appliedRestrictedCredits.some((c) => Math.abs(c.amount) > 0))
    ) {
      const travelWalletItems = [];
      if (travelWalletItemsToApply.offerToApply && offerAmountToApply) {
        travelWalletItems.push({
          title: textConstants.TRAVEL_OFFER_APPLIED,
          value: getTotalPriceText({
            price: {
              value: offerAmountToApply * -1,
              currencyCode:
                travelWalletItemsToApply.offerToApply?.amount.currency,
              currencySymbol: CurrencyFormatters.getSymbol(
                travelWalletItemsToApply.offerToApply?.amount.currency,
              ),
            },
            priceFormatter: twoDecimalFormatter,
          }),
          isTravelOffer: true,
          isTravelCredit: false,
          icon: "offer-tag",
        });
      }
      const hasRestrictedApplied = appliedRestrictedCredits.some((c) => Math.abs(c.amount) > 0);
      const totalRestrictedApplied = appliedRestrictedCredits.reduce(
        (sum, c) => sum + Math.abs(c.amount), 0,
      );
      let firstEligibleRestrictedCredit: StatementCreditDetail | undefined;
      const restrictedCreditBreakdownItems = appliedRestrictedCredits
        .filter((applied) => Math.abs(applied.amount) > 0)
        .map((applied) => {
          const eligibleCredit = eligibleRestrictedCredits.find(
            (c) => c.capOneCreditId.value === applied.capOneCreditId,
          );
          if (!eligibleCredit) return null;
          firstEligibleRestrictedCredit =
            firstEligibleRestrictedCredit ?? eligibleCredit;
          const [restrictedCreditBreakdown] = getCheckoutCreditBreakdown(
            [eligibleCredit],
            Math.abs(applied.amount),
            eligibleCredit.usableAmount.currency,
          );
          return restrictedCreditBreakdown ?? null;
        })
        .filter(
          (item): item is { label: string; formattedAmount: string; amount: number } =>
            !!item,
        );
      if (
        (travelWalletItemsToApply.creditToApply && creditAmountToApply) ||
        hasRestrictedApplied
      ) {
        const breakdownHasStatementCredit =
          !!travelWalletItemsToApply.creditToApply?.breakdown?.some(
            (detail) =>
              detail.CreditDetail === "Statement" &&
              Math.abs(detail.usableAmount.amount) > 0,
          );
        const lineCurrency =
          travelWalletItemsToApply.creditToApply?.amount.currency ??
          firstEligibleRestrictedCredit?.usableAmount.currency ??
          "USD";
        travelWalletItems.push({
          title: breakdownHasStatementCredit || hasRestrictedApplied
            ? textConstants.TOTAL_TRAVEL_CREDITS_APPLIED
            : textConstants.TRAVEL_CREDITS_APPLIED,
          value: getTotalPriceText({
            price: {
              value: (creditAmountToApply + totalRestrictedApplied) * -1,
              currencyCode: lineCurrency,
              currencySymbol: CurrencyFormatters.getSymbol(lineCurrency),
            },
            priceFormatter: twoDecimalFormatter,
          }),
          isTravelOffer: false,
          isTravelCredit: true,
          icon: "piggy-bank-icon",
          className: "wallet-item",
          travelCreditBreakdown: [
            ...restrictedCreditBreakdownItems,
            ...(breakdownHasStatementCredit
              ? getCheckoutCreditBreakdown(
                travelWalletItemsToApply.creditToApply?.breakdown || [],
                creditAmountToApply,
                travelWalletItemsToApply.creditToApply?.amount.currency || lineCurrency,
              )
              : []),
          ],
        });
      }

      return [
        ...travelWalletItems,
        ...rewardsLineItems,
        ...(isPayLater ? [] : [amountDuePriceLine]),
      ];
    }

    return [...rewardsLineItems, ...(isPayLater ? [] : [amountDuePriceLine])];
  },
);

export enum Progress {
  NOT_STARTED = 0,
  IN_PROGRESS = 1,
  COMPLETED = 2,
}

export const getCombinedBookingSteps = createSelector(
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getUserSelectedTravelerId,
  getSelectedPaymentMethodId,
  getRewardsPaymentAccountReferenceId,
  getIsCreditCardPaymentRequired,
  getAllowRewardsWithPolicy,
  (
    email,
    phoneNumber,
    traveler,
    selectedPayment,
    selectedRewardsAccount,
    creditCardRequired,
    canRedeemRewards,
  ) => {
    const paymentCompleted = () => {
      const unsettledPayment = !selectedPayment && creditCardRequired;

      if (selectedRewardsAccount && unsettledPayment) {
        return Progress.IN_PROGRESS;
      }
      if (!selectedRewardsAccount && !selectedPayment) {
        return Progress.NOT_STARTED;
      }
      return Progress.COMPLETED;
    };
    return [
      {
        name: textConstants.TRAVELER_INFORMATION,
        status:
          !!traveler &&
            email &&
            phoneNumber &&
            emailRegex.test(email) &&
            phoneRegex.test(phoneNumber)
            ? Progress.COMPLETED
            : Progress.IN_PROGRESS,
      },
      {
        name:
          canRedeemRewards || !isCorpTenant(config.TENANT)
            ? textConstants.REWARDS_AND_PAYMENT
            : textConstants.CORP_REWARDS_AND_PAYMENT_SHORTENED,
        status: paymentCompleted(),
      },
    ];
  },
);

export const getBookingProgress = createSelector(
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getUserSelectedTravelerId,
  getSelectedPaymentMethodId,
  getRewardsPaymentAccountReferenceId,
  getIsCreditCardPaymentRequired,
  getAllowRewardsWithPolicy,
  (
    email,
    phoneNumber,
    traveler,
    selectedPayment,
    selectedRewardsAccount,
    creditCardRequired,
    canRedeemRewards,
  ) => {
    const paymentCompleted = () => {
      const unsettledPayment = !selectedPayment && creditCardRequired;

      if (selectedRewardsAccount && unsettledPayment) {
        return Progress.IN_PROGRESS;
      }
      if (!selectedRewardsAccount && !selectedPayment) {
        return Progress.NOT_STARTED;
      }
      return Progress.COMPLETED;
    };
    return [
      {
        name: textConstants.ADD_TRAVELERS,
        status: traveler ? Progress.COMPLETED : Progress.IN_PROGRESS,
      },
      {
        name: textConstants.CONTACT,
        status:
          email &&
            phoneNumber &&
            emailRegex.test(email) &&
            phoneRegex.test(phoneNumber)
            ? Progress.COMPLETED
            : Progress.NOT_STARTED,
      },
      {
        name:
          canRedeemRewards || !isCorpTenant(config.TENANT)
            ? textConstants.REWARDS_AND_PAYMENT
            : textConstants.CORP_REWARDS_AND_PAYMENT_SHORTENED,
        status: paymentCompleted(),
      },
    ];
  },
);

export const getBookingInProgress = createSelector(
  getSchedulePriceQuoteCallState,
  getPollPriceQuoteCallState,
  getScheduleBookCallState,
  getConfirmationDetailsCallState,
  (scheduleQuote, pollPriceQuote, scheduleBook, confirmDetails) =>
    scheduleQuote === CallState.InProcess ||
    pollPriceQuote === CallState.InProcess ||
    scheduleBook === CallState.InProcess ||
    confirmDetails === CallState.InProcess,
);

// CT1261T-419 connection-retry banner state. PC has no reshop funnel, so the
// shared "requote" selectors collapse to the connection-retry flag only.
export const getShowConnectionRetryBanner = (state: IStoreState) =>
  state.premierCollectionBook.showConnectionRetryBanner;

export const getIsConnectionRetryPending = (state: IStoreState) =>
  state.premierCollectionBook.isConnectionRetryPending;

export const getShowRequoteSuccessBanner = getShowConnectionRetryBanner;

export const getIsRequotePending = getIsConnectionRetryPending;

// CT1261T-419: guard the *rendered* banner without clearing the stored flag
// (which persists through a later book attempt/failure and would otherwise let
// the sticky banner paint over BookingErrorModal). Mirrors the decrease notice.
export const getShowConnectionRetrySuccessBanner = createSelector(
  getShowConnectionRetryBanner,
  getHasError,
  getBookingInProgress,
  (showConnectionRetryBanner, hasError, bookingInProgress): boolean =>
    showConnectionRetryBanner && !hasError && !bookingInProgress,
);

// CT1261T-421: drives the non-blocking inline decrease notice. Shown only when
// the decrease is the sole thing on screen — not while a blocking error modal
// (getHasError) or the requote success banner owns the sticky slot, and not
// while quote/book/confirmation processing is active (the BookingInProgress
// dialog, which the sticky notice would otherwise paint over on mobile).
export const getShowPriceDecreaseMessage = createSelector(
  getPriceDifference,
  isLodgingErrorCopyUpdateEnabledSelector,
  getHasError,
  getShowRequoteSuccessBanner,
  getBookingInProgress,
  (
    priceDifference,
    isLodgingErrorCopyUpdateEnabled,
    hasError,
    showRequoteSuccessBanner,
    bookingInProgress,
  ): boolean =>
    priceDifference.hasDifference &&
    !isPriceDifferenceBlocking(
      priceDifference,
      isLodgingErrorCopyUpdateEnabled,
    ) &&
    !hasError &&
    !showRequoteSuccessBanner &&
    !bookingInProgress,
);

export const getIsBookingValid = createSelector(
  getPriceDifference,
  getPriceDifferenceAcknowledged,
  getIsCreditCardPaymentRequired,
  getSelectedPaymentMethodId,
  getUserSelectedTravelerId,
  getBookingInProgress,
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getIsTravelWalletPaymentOnly,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    priceDifference,
    priceDifferenceAcknowledged,
    creditCardRequired,
    selectedPaymentMethodId,
    userSelectedTravelerId,
    bookingInProgress,
    email,
    phone,
    isTravelWalletPaymentOnly,
    isLodgingErrorCopyUpdateEnabled,
  ) => {
    const paymentUnsettled =
      creditCardRequired &&
      !selectedPaymentMethodId &&
      !isTravelWalletPaymentOnly;

    if (
      // CT1261T-421: a non-blocking decrease must not block booking.
      (priceDifference.hasDifference &&
        !priceDifferenceAcknowledged &&
        isPriceDifferenceBlocking(
          priceDifference,
          isLodgingErrorCopyUpdateEnabled,
        )) ||
      paymentUnsettled ||
      !userSelectedTravelerId ||
      bookingInProgress ||
      !email ||
      !phone
    ) {
      return false;
    }
    return true;
  },
);

export const getEarn = (state: IStoreState) =>
  state.premierCollectionBook.productEarnValue;

const getPaymentRequestType = createSelector(
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  (
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    selectedPaymentMethodId,
  ) => {
    if (selectedPaymentMethodId && !rewardsPayment) {
      return PaymentSplitRequestEnum.PaymentCardRequest;
    }
    if (
      rewardsAccountId &&
      rewardsPayment &&
      rewardsFiatPayment &&
      creditCardPayment?.value === 0
    ) {
      return PaymentSplitRequestEnum.PaymentRewardsRequest;
    }
    if (rewardsPayment && rewardsAccountId && rewardsFiatPayment) {
      return PaymentSplitRequestEnum.PaymentCardRewardsRequest;
    }
    return null;
  },
);

export const getCardPaymentRewardsAccount = createSelector(
  getPaymentMethodRewardsAccountId,
  getRewardsAccounts,
  (accountId, rewardsAccounts) =>
    rewardsAccounts.find((account) => account.accountReferenceId === accountId),
);

export const getEarnedString = createSelector(
  getEarn,
  getCardPaymentRewardsAccount,
  (earn, account) => {
    if (
      !earn ||
      (isCorpTenant(config.TENANT) &&
        account?.customerAccountRole !== "Primary")
    )
      return "";
    return textConstants.EARNED_STRING(
      earn,
      account?.rewardsBalance.currencyDescription,
    );
  },
);

export const getPaymentRequest = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getPaymentRequestType,
  getSession,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    session,
  ): PaymentSplitRequest | null => {
    let amount: PaymentType | null = null;
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        const userCardPayment: UserCardPaymentType = {
          paymentId: paymentId || "",
          accountReferenceId,
          paymentAmount: {
            currency: creditCardPayment!.currencyCode,
            amount: roundToTwoDecimals(creditCardPayment!.value),
            PaymentAmount: PaymentAmountEnum.FiatAmount,
          },
          Payment: TypeOfPaymentEnum.UserCard,
        };
        amount = userCardPayment;
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        const splitPayment: SplitPaymentType = {
          paymentId: paymentId || "",
          accountReferenceId,
          paymentAmount: {
            fiatAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
              PaymentAmount: PaymentAmountEnum.FiatAmount,
            },
            rewardsAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
              PaymentAmount: PaymentAmountEnum.RewardsAmount,
            },
            PaymentAmount: PaymentAmountEnum.SplitAmount,
          },
          Payment: TypeOfPaymentEnum.Split,
        };
        amount = splitPayment;
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        const rewardsPaymentType: RewardsPaymentType = {
          paymentAmount: {
            rewardsAccountId: rewardsAccountId!,
            fiatValue: {
              amount: roundToTwoDecimals(rewardsFiatPayment!.value),
              currency: rewardsFiatPayment!.currencyCode,
            },
            rewardsPrice: {
              value: roundToTwoDecimals(rewardsPayment!.value),
              currency: rewardsPayment!.currency,
            },
            PaymentAmount: PaymentAmountEnum.RewardsAmount,
          },
          Payment: TypeOfPaymentEnum.Rewards,
        };
        amount = rewardsPaymentType;
        break;
      default:
        return null;
    }

    if (amount && session) {
      const request = {
        token: session,
        payment: amount,
        ancillaries: [],
      };
      return request;
    }
    return null;
  },
);

export const getOpaquePayments = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getPaymentRequestType,
  getTravelWalletItemsToApply,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletCreditToApplyAmount,
  getEligibleRestrictedPcCredits,
  getAppliedRestrictedCredits,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    travelWalletItemsToApply,
    offerToApplyAmount,
    creditToApplyAmount,
    eligibleRestrictedCredits,
    appliedRestrictedCredits,
  ): PaymentOpaqueValue[] | null => {
    const amount: PaymentOpaqueValue[] = [];
    const excludedCapOneCreditIds = eligibleRestrictedCredits.map(
      (c) => c.capOneCreditId.value,
    );
    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply
    ) {
      if (travelWalletItemsToApply.offerToApply) {
        const offer: PaymentOpaqueValue = {
          type: Payment.TravelWalletOffer,
          value: {
            offerId: travelWalletItemsToApply.offerToApply.id,
            description:
              travelWalletItemsToApply.offerToApply?.descriptions[0] || "",
            paymentAmount: {
              fiatValue: {
                amount: roundToTwoDecimals(offerToApplyAmount),
                currency: travelWalletItemsToApply.offerToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletOffer,
          },
        };
        amount.push(offer);
      }
      if (travelWalletItemsToApply.creditToApply && creditToApplyAmount > 0) {
        const credit: PaymentOpaqueValue = {
          type: Payment.TravelWalletCredit,
          value: {
            offerId: travelWalletItemsToApply.creditToApply.id,
            description: "TravelWalletCredit", // creditToApply doesn't have a description
            ...(excludedCapOneCreditIds.length > 0
              ? { excludedCapOneCreditIds }
              : {}),
            paymentAmount: {
              fiatValue: {
                amount: roundToTwoDecimals(creditToApplyAmount),
                currency:
                  travelWalletItemsToApply.creditToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletCredit,
          },
        };
        amount.push(credit);
      }
    }
    const restrictedPayments: PaymentOpaqueValue[] = appliedRestrictedCredits
      .filter((applied) => Math.abs(applied.amount) > 0)
      .flatMap((applied) => {
        const eligibleCredit = eligibleRestrictedCredits.find(
          (c) => c.capOneCreditId.value === applied.capOneCreditId,
        );
        if (!eligibleCredit) return [];
        const restrictedCredit: PaymentOpaqueValue = {
          type: Payment.TravelWalletRestrictedCredit,
          value: {
            capOneCreditId: eligibleCredit.capOneCreditId.value,
            description: "TravelWalletRestrictedCredit",
            paymentAmount: {
              fiatValue: {
                amount: roundToTwoDecimals(Math.abs(applied.amount)),
                currency: eligibleCredit.usableAmount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletRestrictedCredit,
          } satisfies TravelWalletRestrictedCreditPayment,
        };
        return [restrictedCredit];
      });
    amount.push(...restrictedPayments);
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        const userCardPayment: PaymentOpaqueValue = {
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          },
        };
        amount.push(userCardPayment);
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        const splitUserCardPayment: PaymentOpaqueValue = {
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          },
        };

        const splitRewardsPayment: PaymentOpaqueValue = {
          type: Payment.Rewards,
          value: {
            paymentAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
            },
            PaymentV2: PaymentV2Enum.Rewards,
          },
        };

        amount.push(splitUserCardPayment, splitRewardsPayment);
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        const rewardsPaymentType: PaymentOpaqueValue = {
          type: Payment.Rewards,
          value: {
            paymentAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
            },
            PaymentV2: PaymentV2Enum.Rewards,
          },
        };
        amount.push(rewardsPaymentType);
        break;
      default:
        break;
    }

    if (amount) {
      return amount;
    }
    return null;
  },
);

export const getPaymentRequestV2 = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getPaymentRequestType,
  getSession,
  getTravelWalletItemsToApply,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletCreditToApplyAmount,
  getEligibleRestrictedPcCredits,
  getAppliedRestrictedCredits,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    session,
    travelWalletItemsToApply,
    offerToApplyAmount,
    creditToApplyAmount,
    eligibleRestrictedCredits,
    appliedRestrictedCredits,
  ): PaymentSplitRequestV2 | null => {
    const amount: PaymentV2[] = [];
    const excludedCapOneCreditIds = eligibleRestrictedCredits.map(
      (c) => c.capOneCreditId.value,
    );
    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply
    ) {
      if (travelWalletItemsToApply.offerToApply) {
        const offer: TravelWalletOfferPayment = {
          offerId: travelWalletItemsToApply.offerToApply.id,
          description:
            travelWalletItemsToApply.offerToApply?.descriptions[0] || "",
          paymentAmount: {
            fiatValue: {
              amount: roundToTwoDecimals(offerToApplyAmount),
              currency: travelWalletItemsToApply.offerToApply.amount.currency,
            },
          },
          PaymentV2: PaymentV2Enum.TravelWalletOffer,
        };
        amount.push(offer);
      }
      if (travelWalletItemsToApply.creditToApply && creditToApplyAmount > 0) {
        const credit: TravelWalletCreditPayment = {
          offerId: travelWalletItemsToApply.creditToApply.id,
          description: "TravelWalletCredit", // creditToApply doesn't have a description
          ...(excludedCapOneCreditIds.length > 0
            ? { excludedCapOneCreditIds }
            : {}),
          paymentAmount: {
            fiatValue: {
              amount: roundToTwoDecimals(creditToApplyAmount),
              currency: travelWalletItemsToApply.creditToApply.amount.currency,
            },
          },
          PaymentV2: PaymentV2Enum.TravelWalletCredit,
        };
        amount.push(credit);
      }
    }
    const restrictedPayments: TravelWalletRestrictedCreditPayment[] = appliedRestrictedCredits
      .filter((applied) => Math.abs(applied.amount) > 0)
      .flatMap((applied) => {
        const eligibleCredit = eligibleRestrictedCredits.find(
          (c) => c.capOneCreditId.value === applied.capOneCreditId,
        );
        if (!eligibleCredit) return [];
        const restrictedCredit: TravelWalletRestrictedCreditPayment = {
          capOneCreditId: eligibleCredit.capOneCreditId.value,
          description: "TravelWalletRestrictedCredit",
          paymentAmount: {
            fiatValue: {
              amount: roundToTwoDecimals(Math.abs(applied.amount)),
              currency: eligibleCredit.usableAmount.currency,
            },
          },
          PaymentV2: PaymentV2Enum.TravelWalletRestrictedCredit,
        };
        return [restrictedCredit];
      });
    amount.push(...restrictedPayments);
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        const userCardPayment: UserCard = {
          paymentId: paymentId || "",
          accountReferenceId,
          paymentAmount: {
            currency: creditCardPayment!.currencyCode,
            amount: roundToTwoDecimals(creditCardPayment!.value),
          },
          PaymentV2: PaymentV2Enum.UserCard,
        };
        amount.push(userCardPayment);
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        const splitUserCardPayment: UserCard = {
          paymentId: paymentId || "",
          accountReferenceId,
          paymentAmount: {
            currency: creditCardPayment!.currencyCode,
            amount: roundToTwoDecimals(creditCardPayment!.value),
          },
          PaymentV2: PaymentV2Enum.UserCard,
        };

        const splitRewardsPayment: Rewards = {
          paymentAmount: {
            rewardsAccountId: rewardsAccountId!,
            fiatValue: {
              amount: roundToTwoDecimals(rewardsFiatPayment!.value),
              currency: rewardsFiatPayment!.currencyCode,
            },
            rewardsPrice: {
              value: roundToTwoDecimals(rewardsPayment!.value),
              currency: rewardsPayment!.currency,
            },
          },
          PaymentV2: PaymentV2Enum.Rewards,
        };

        amount.push(splitUserCardPayment, splitRewardsPayment);
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        const rewardsPaymentType: Rewards = {
          paymentAmount: {
            rewardsAccountId: rewardsAccountId!,
            fiatValue: {
              amount: roundToTwoDecimals(rewardsFiatPayment!.value),
              currency: rewardsFiatPayment!.currencyCode,
            },
            rewardsPrice: {
              value: roundToTwoDecimals(rewardsPayment!.value),
              currency: rewardsPayment!.currency,
            },
          },
          PaymentV2: PaymentV2Enum.Rewards,
        };
        amount.push(rewardsPaymentType);
        break;
      default:
        break;
    }

    if (amount && session) {
      const request = {
        token: session,
        payments: amount,
        ancillaries: [],
      };
      return request;
    }
    return null;
  },
);

export const getCardPaymentAccount = createSelector(
  getRewardsAccounts,
  getPaymentRequest,
  (rewardsAccounts, paymentRequest: PaymentSplitRequest | null) => {
    switch (paymentRequest?.payment.Payment) {
      case TypeOfPaymentEnum.UserCard:
        return rewardsAccounts.find(
          (acc) =>
            acc.accountReferenceId ===
            (paymentRequest?.payment as UserCardPaymentType).accountReferenceId,
        );
      case TypeOfPaymentEnum.Split:
        return rewardsAccounts.find(
          (acc) =>
            acc.accountReferenceId ===
            (paymentRequest?.payment as SplitPaymentType).accountReferenceId,
        );
      default:
        return null;
    }
  },
);

export enum CheckoutSteps {
  ADD_TRAVELERS = 0,
  CONTACT = 1,
  REWARDS_AND_PAYMENT = 2,
}

export const isTravelerStepComplete = createSelector(
  getBookingProgress,
  (checkoutProgress) =>
    checkoutProgress[CheckoutSteps.ADD_TRAVELERS].status === Progress.COMPLETED,
);

export const isContactStepComplete = createSelector(
  getBookingProgress,
  (checkoutProgress) =>
    checkoutProgress[CheckoutSteps.CONTACT].status === Progress.COMPLETED,
);

export const areAllStepsCompletedInCheckout = createSelector(
  getBookingProgress,
  getIsTravelWalletPaymentOnly,
  (checkoutProgress, isTravelWalletPaymentOnly) =>
    checkoutProgress[CheckoutSteps.ADD_TRAVELERS].status ===
    Progress.COMPLETED &&
    checkoutProgress[CheckoutSteps.CONTACT].status === Progress.COMPLETED &&
    (!isTravelWalletPaymentOnly
      ? checkoutProgress[CheckoutSteps.REWARDS_AND_PAYMENT].status ===
      Progress.COMPLETED
      : true),
);

export const verifyPaymentMethodResultSelector = (state: IStoreState) =>
  state.premierCollectionBook.verifyPaymentMethodResult;

export const getModalType = createSelector(
  getPriceQuoteErrors,
  getScheduleBookError,
  getSchedulePriceQuoteError,
  getConfirmationDetailsError,
  getPriceDifference,
  verifyPaymentMethodResultSelector,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    priceQuoteErrors,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsErrors,
    priceDifference,
    verifyPaymentMethodResult,
    isLodgingErrorCopyUpdateEnabled,
  ): string => {
    // Only a blocking price difference renders (and tracks) a modal; a
    // non-blocking decrease is the inline notice (CT1261T-421).
    if (
      priceDifference.hasDifference &&
      priceDifference.amount &&
      isPriceDifferenceBlocking(priceDifference, isLodgingErrorCopyUpdateEnabled)
    ) {
      return "pc_price_difference";
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      return "payment_verification_failed";
    }

    if (confirmationDetailsErrors.length > 0) {
      return "confirmation_details_failed";
    }

    if (scheduleBookError) {
      return "schedule_book_failed";
    }

    if (schedulePriceQuoteError) {
      return "schedule_price_quote_failed";
    }

    if (priceQuoteErrors.length > 0) {
      return "poll_price_quote_failed";
    }

    return "";
  },
);

export const getErrorTitles = createSelector(
  getHasError,
  getPriceQuoteErrors,
  getScheduleBookError,
  getSchedulePriceQuoteError,
  getConfirmationDetailsError,
  getPriceDifference,
  verifyPaymentMethodResultSelector,
  getTravelWalletItemsToApply,
  getModalType,
  getRewardsAccounts,
  isLodgingErrorCopyUpdateEnabledSelector,
  (
    hasError,
    priceQuoteErrors,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsErrors,
    priceDifference,
    verifyPaymentMethodResult,
    travelWalletItemsToApply,
    modalType,
    accounts,
    isLodgingErrorCopyUpdateEnabled,
  ): ResolvedErrorTitles => {
    if (!hasError) return { title: "", subtitle: "", primaryButtonText: "" };

    const properties: ModalAlertProperties = {
      type: modalType,
      category: ModalCategoryType.TROUBLE,
      screen: ModalScreens.HOTELS_CHECKOUT,
      primary_button: "",
      secondary_button: "",
      modal_subtitle: "",
      modal_title: "",
      agent_title: "",
      agent_subtitle: "",
      step: "",
      funnel: "premier_collection",
    };

    const callTrackEvent = (properties: ModalAlertProperties) => {
      trackEvent({
        eventName: MODAL_ALERT,
        properties,
      });
    };

    // Mirror the hotels selector: copy the resolved title/subtitle/button text
    // onto the tracking payload before firing, so modal_alert never reports
    // empty modal_title/modal_subtitle/agent_* (see hotels index.ts:2568-2576).
    const applyErrorTitleTracking = (returnProps: ErrorTitles, step: string) => {
      properties.primary_button = returnProps.primaryButtonText || "";
      properties.secondary_button = returnProps.secondaryButtonText || "";
      properties.modal_subtitle = getSubtitleTrackingValue(returnProps);
      properties.modal_title = returnProps.title || "";
      properties.agent_subtitle =
        returnProps.agentSubtitle || properties.modal_subtitle;
      properties.agent_title = returnProps.agentTitle || properties.modal_title;
      properties.step = step;
    };

    // CT1261T-421: only a blocking price difference owns the modal; a non-blocking
    // decrease is the inline notice, so let a co-occurring error show through.
    if (
      priceDifference.hasDifference &&
      priceDifference.amount &&
      isPriceDifferenceBlocking(priceDifference, isLodgingErrorCopyUpdateEnabled)
    ) {
      const primaryButtonText = textConstants.CONTINUE;

      const offerAmount =
        travelWalletItemsToApply.offerToApply?.amount.amount || 0;
      const creditsAmount =
        travelWalletItemsToApply.creditToApply?.amount.amount || 0;

      const walletItemCoverageChanged =
        (travelWalletItemsToApply.offerToApply ||
          travelWalletItemsToApply.creditToApply) &&
        Math.abs(offerAmount + creditsAmount) >=
        priceDifference.predictedTotal &&
        Math.abs(offerAmount + creditsAmount) < priceDifference.priceQuoteTotal;

      if (walletItemCoverageChanged) {
        const returnProps: ErrorTitles = {
          title: travelWalletItemsToApply.creditToApply
            ? textConstants.PRICE_INCREASE_CREDITS_COVERAGE_TITLE
            : textConstants.PRICE_INCREASE_OFFER_COVERAGE_TITLE,
          subtitle: textConstants.PRICE_INCREASE_WALLET_COVERAGE_SUBTITLE,
          primaryButtonText,
          secondaryButtonText: textConstants.CHOOSE_ANOTHER_HOTEL,
          icon: priceDifference.isIncrease
            ? textConstants.PRICE_INCREASE_ICON
            : textConstants.PRICE_DECREASE_ICON,
        };
        applyErrorTitleTracking(returnProps, "save_travelers");
        callTrackEvent(properties);
        return returnProps;
      }

      // CT1261T-420: refreshed increase modal. Only the base increase changes;
      // the wallet-coverage sub-branch above keeps its copy.
      const useNewIncreaseModal =
        isLodgingErrorCopyUpdateEnabled && priceDifference.isIncrease;
      // Round the increase UP (ceil); getTotalPriceText renders it in the quote
      // currency. The >= $1 threshold still uses the raw delta. Normalize FP
      // subtraction noise to cents first so an exact whole-dollar delta isn't
      // ceil'd up a dollar.
      const increaseAmountFiat: FiatPrice | undefined = priceDifference.fiatAmount
        ? {
            ...priceDifference.fiatAmount,
            value: Math.ceil(
              Math.round(priceDifference.fiatAmount.value * 100) / 100,
            ),
          }
        : undefined;

      const returnProps: ErrorTitles =
        useNewIncreaseModal && increaseAmountFiat
          ? {
              title: textConstants.PRICE_INCREASE_TITLE_V2(increaseAmountFiat),
              subtitle: textConstants.PRICE_INCREASE_SUBTITLE_V2,
              primaryButtonText: textConstants.ACCEPT_NEW_PRICE,
              secondaryButtonText: textConstants.VIEW_OTHER_OPTIONS,
              icon: textConstants.PRICE_INCREASE_ICON,
            }
          : {
              title: textConstants.getPriceDifferenceTitle(
                priceDifference.isIncrease,
                priceDifference.amount,
              ),
              subtitle: priceDifference.isIncrease
                ? textConstants.PRICE_INCREASE_SUBTITLE
                : textConstants.PRICE_DECREASE_SUBTITLE,
              primaryButtonText,
              icon: priceDifference.isIncrease
                ? textConstants.PRICE_INCREASE_ICON
                : textConstants.PRICE_DECREASE_ICON,
            };
      applyErrorTitleTracking(returnProps, "save_travelers");
      callTrackEvent(properties);
      return returnProps;
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      const returnProps = paymentTextConstants.PAYMENT_METHOD_ERROR_TITLES(
        verifyPaymentMethodResult,
        accounts[0],
        isCorpTenant(config.TENANT),
      );
      applyErrorTitleTracking(returnProps, "select_payment");
      callTrackEvent(properties);
      return { ...returnProps };
    }

    if (confirmationDetailsErrors.length > 0) {
      const returnProps = getErrorMessage({
        error: confirmationDetailsErrors[0],
        isLodgingErrorCopyUpdateEnabled,
      });
      applyErrorTitleTracking(returnProps, "confirm_and_book");
      callTrackEvent(properties);
      return returnProps;
    }

    if (scheduleBookError) {
      // CT1261T-419: connection error on book — retryable modal when flag on.
      const returnProps = isLodgingErrorCopyUpdateEnabled
        ? textConstants.CONNECTION_ERROR_TITLES
        : textConstants.GENERIC_ERROR_TITLES;
      applyErrorTitleTracking(returnProps, "confirm_and_book");
      callTrackEvent(properties);
      return { ...returnProps, isConnection: isLodgingErrorCopyUpdateEnabled };
    }

    if (schedulePriceQuoteError) {
      // CT1261T-419: connection error on quote — retryable modal when flag on.
      const returnProps = isLodgingErrorCopyUpdateEnabled
        ? textConstants.CONNECTION_ERROR_TITLES
        : textConstants.GENERIC_ERROR_TITLES;
      applyErrorTitleTracking(returnProps, "save_travelers");
      callTrackEvent(properties);
      return { ...returnProps, isConnection: isLodgingErrorCopyUpdateEnabled };
    }

    if (priceQuoteErrors.length > 0) {
      const returnProps = getErrorMessage({
        error: priceQuoteErrors[0],
        isLodgingErrorCopyUpdateEnabled,
      });
      applyErrorTitleTracking(returnProps, "confirm_and_book");
      callTrackEvent(properties);
      return returnProps;
    }

    // CT1261T-422: shared fallback for a hasError with no specific branch.
    const returnProps = isLodgingErrorCopyUpdateEnabled
      ? textConstants.FALLBACK_ERROR_TITLES
      : textConstants.GENERIC_ERROR_TITLES;
    applyErrorTitleTracking(returnProps, "save_travelers");
    callTrackEvent(properties);
    return returnProps;
  },
);

// CT1261T-419: reads the isConnection flag resolved by getErrorTitles (single
// source) so the classification isn't re-derived here.
export const getIsConnectionError = createSelector(
  getErrorTitles,
  (errorTitles): boolean => !!errorTitles.isConnection,
);

export const getReviewPremierCollectionDetailsCheckoutProperties =
  createSelector(
    getPremierCollectionShopSelectedAvailability,
    getPremierCollectionShopChosenProduct,
    getFromDate,
    getUntilDate,
    getNightCount,
    getViewedPremierCollectionDetailsProperties,
    getPriceQuotePricing,
    getPaymentMethods,
    getAdultsCount,
    getChildrenCount,
    getPetsCount,
    getPersistentLodgingTrackingProperties,
    (
      hotelAvail,
      product,
      fromDate,
      toDate,
      nights,
      viewedPremierCollectionDetailsProperties,
      priceQuotePricing,
      paymentMethods,
      adultsCount,
      childrenCount,
      petsCount,
      getPersistentLodgingTrackingProperties,
    ): ITrackingProperties<
      Omit<ReviewDetailsHotelCheckoutProperties, "pc_shown" | "lc_shown">
    > => {
      const productTaxes = product
        ? Object.keys(product.taxBreakdown).reduce(
          (sum, key) => product.taxBreakdown[key].fiat.value + sum,
          0,
        )
        : 0;

      return {
        properties: {
          lodging_name: hotelAvail?.lodging.name || "",
          chain: "",
          brand: "",
          length_of_stay: nights || 0,
          sell_price_excluding_taxes_and_fees_usd:
            priceQuotePricing?.subtotal.fiat.value ||
            product?.tripTotal.fiat.value ||
            0,
          owed_now_excluding_taxes_and_fees:
            priceQuotePricing?.subtotal.fiat.value ||
            product?.tripTotal.fiat.value ||
            0,
          owed_now_taxes_and_fees:
            priceQuotePricing?.taxes.fiat.value || productTaxes || 0,
          owed_at_checkin_taxes_and_fees:
            priceQuotePricing?.taxes?.fiat.value || productTaxes || 0,
          pay_later_excluding_taxes_and_fees:
            priceQuotePricing?.taxes?.fiat.value || productTaxes || 0,
          pay_later_taxes_and_fees:
            priceQuotePricing?.taxes?.fiat.value || productTaxes || 0,
          pay_later_fees:
            priceQuotePricing?.feeBreakdown.total?.value ||
            product?.feeBreakdown.total?.value ||
            0,
          total_discount: 0,
          refundability:
            product?.cancellationPolicy.CancellationPolicy ===
              CancellationPolicyEnum.NonRefundable
              ? "non_refundable"
              : "free_cancellation",
          supply_revenue_usd:
            priceQuotePricing?.subtotal.fiat.value ||
            product?.tripTotal.fiat.value ||
            0,
          check_in_date: fromDate ? dayjs(fromDate).format("MM/DD/YYYY") : "",
          check_out_date: toDate ? dayjs(toDate).format("MM/DD/YYYY") : "",
          supplier: "",
          provider_type: "direct", // // TODO find out where this comes from -> "direct" | "retail" | "wholesale",
          adults_count: adultsCount,
          children_count: childrenCount,
          pets_searched: petsCount,
          adr: "",
          ...viewedPremierCollectionDetailsProperties.properties,
          ...hotelAvail?.trackingPropertiesV2?.properties,
          card_on_file: paymentMethods.length > 0,
          payment_schedule: product?.roomPaymentSchedule,
          ...getPersistentLodgingTrackingProperties,
        },
        encryptedProperties: [
          ...viewedPremierCollectionDetailsProperties.encryptedProperties,
          hotelAvail?.trackingPropertiesV2?.encryptedProperties ?? "",
        ],
      };
    },
  );

export const getSelectedPaymentCardType = createSelector(
  getRewardsAccounts,
  getPaymentMethods,
  getSelectedPaymentMethodId,
  (accounts, paymentMethods, id): string => {
    const paymentMethod = paymentMethods.find((p) => p.id === id);
    if (
      accounts[0]?.customerAccountRole === CustomerAccountRole.NonFinancialUser
    ) {
      return paymentMethod?.cardType ?? "";
    }
    return (
      accounts.find(
        (a) =>
          a.lastFour === paymentMethod?.last4 ||
          a.lastFourVirtualCardNumbers?.includes(paymentMethod?.last4 || ""),
      )?.productDisplayName || ""
    );
  },
);

export const getPriceQuotePremierCollectionProperties = createSelector(
  getReviewPremierCollectionDetailsCheckoutProperties,
  getSession,
  getSelectedLodgingIndex,
  getTravelWalletItemsToApply,
  getPriceDifference,
  getPersistentLodgingTrackingProperties,
  getEligibleRestrictedPcCredits,
  (
    reviewPremierCollectionDetailsProperties,
    session,
    selectedLodgingIndex,
    travelWalletItemsToApply,
    priceDifference,
    persistentLodgingTrackingProperties,
    eligibleRestrictedPcCredits,
  ): ITrackingProperties<PriceQuotePremierCollectionProperties> => ({
    properties: {
      ...reviewPremierCollectionDetailsProperties.properties,
      success: false,
      booking_session_id: session?.value || "",
      lodging_row_index:
        selectedLodgingIndex != null ? selectedLodgingIndex : undefined,
      ...travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.properties,
      ...travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.properties,
      x_price_difference:
        parseInt(priceDifference?.amount ?? "0", 10) *
        (priceDifference?.isIncrease ? 1 : -1),
      payment_schedule:
        reviewPremierCollectionDetailsProperties.properties.payment_schedule,
      ...persistentLodgingTrackingProperties,
      has_restricted_credits: eligibleRestrictedPcCredits.length > 0,
      restricted_credit_balance: eligibleRestrictedPcCredits.reduce(
        (sum, c) => sum + Math.abs(c.usableAmount.amount),
        0,
      ),
    },
    encryptedProperties: [
      ...reviewPremierCollectionDetailsProperties.encryptedProperties,
      travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
      travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
    ],
  }),
);

export const getIsPaymentMethodVCN = createSelector(
  getPaymentMethodRewardsAccountId,
  getPaymentMethod,
  getRewardsAccounts,
  (paymentRewardsAccountId, paymentMethod, rewardsAccounts) => {
    if (!paymentRewardsAccountId || !paymentMethod || !rewardsAccounts)
      return false;

    const account = rewardsAccounts.find(
      (account) => account.accountReferenceId === paymentRewardsAccountId,
    );

    return !!(
      account?.lastFourVirtualCardNumbers &&
      paymentMethod?.last4 &&
      account?.lastFourVirtualCardNumbers?.includes(paymentMethod?.last4)
    );
  },
);

export const getDatelessSearchTrackingProperty = createSelector(
  getIsFromHotelDatelessSearch,
  (
    isFromHotelDatelessSearch,
  ): ITrackingProperties<DatelessSearchProperties> => ({
    properties: {
      is_dateless: isFromHotelDatelessSearch ?? false,
    },
    encryptedProperties: [],
  }),
);

export const getIsFromPremiumOffersTrackingProperty = createSelector(
  getIsFromPremiumOffers,
  (isFromPremiumOffers): PremiumOffersPageProperties => ({
    is_from_premium_offers_page: isFromPremiumOffers ?? false,
  }),
);

export const getHotelDetailsEntrySource = (state: IStoreState) =>
  state.premierCollectionShop.hotelDetailsEntrySource;

export const getCompleteBuyPremierCollectionProperties = createSelector(
  getReviewPremierCollectionDetailsCheckoutProperties,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentAccount,
  getSelectedPaymentCardType,
  getConfirmationDetails,
  getSession,
  getSelectedLodgingIndex,
  getOffers,
  getTravelWalletCreditToApplyAmount,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletItemsToApply,
  getIsPaymentMethodVCN,
  getDatelessSearchTrackingProperty,
  getPriceDifference,
  getPersistentLodgingTrackingProperties,
  getSelectedLodgingIsFavorited,
  getHotelDetailsEntrySource,
  getIsSelfBooked,
  getAppliedRestrictedCredits,
  getTotalRestrictedCreditAmountApplied,
  (
    reviewProperties,
    cardPayment,
    rewardPayment,
    rewardCurrencyPayment,
    account,
    cardType,
    reservation,
    session,
    selectedLodgingIndex,
    offers,
    creditToApplyAmount,
    offerToApplyAmount,
    travelWalletItemsToApply,
    isPaymentMethodVCN,
    datelessSearchProperties,
    priceDifference,
    persistentLodgingTrackingProperties,
    selectedLodgingIsFavorited,
    hotelDetailsEntrySource,
    isSelfBooked,
    appliedRestrictedCredits,
    totalRestrictedCreditAmountApplied,
  ): ITrackingProperties<
    | CompleteBuyPremierCollectionProperties
    | CompleteBuyLifestyleCollectionProperties
  > => ({
    properties: {
      ...reviewProperties.properties,
      success: false,
      total_rewards_amount_usd: rewardPayment?.value || 0,
      total_card_amount_usd: cardPayment?.value || 0,
      card_product_used: cardType,
      rewards_product_used: account?.productDisplayName || "",
      rewards_currency: rewardCurrencyPayment?.currency || "",
      agent_booking_fee_amount_usd: 0,
      booking_session_id: session?.value || "",
      reservation_id: reservation?.reservationId || "",
      lodging_row_index:
        selectedLodgingIndex != null ? selectedLodgingIndex : undefined,
      ...travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.properties,
      ...travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.properties,
      offer_amount_redeemed_usd:
        !!travelWalletItemsToApply.offerToApply && !!offerToApplyAmount
          ? Math.abs(offerToApplyAmount)
          : 0,
      offer_count: offers?.length,
      offer_used: !!travelWalletItemsToApply.offerToApply,
      rooms_booked: reviewProperties.properties.rooms_searched,
      credit_redeemed: !!travelWalletItemsToApply.creditToApply?.amount.amount,
      credit_amt_redeemed:
        !!travelWalletItemsToApply.creditToApply && !!creditToApplyAmount
          ? Math.abs(creditToApplyAmount)
          : 0,
      restricted_credit_redeemed: appliedRestrictedCredits.some((c) => Math.abs(c.amount) > 0),
      restricted_credit_amt_redeemed: Math.abs(totalRestrictedCreditAmountApplied),
      is_vcn: isPaymentMethodVCN,
      ...datelessSearchProperties.properties,
      x_price_difference:
        parseInt(priceDifference?.amount ?? "0", 10) *
        (priceDifference?.isIncrease ? 1 : -1),
      payment_schedule: reviewProperties.properties.payment_schedule,
      is_favorited: selectedLodgingIsFavorited ?? false,
      hotel_details_entry_source: hotelDetailsEntrySource || undefined,
      ...persistentLodgingTrackingProperties,
      booked_self_name_match: isSelfBooked,
    },
    encryptedProperties: [
      ...reviewProperties.encryptedProperties,
      travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
      travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
      ...datelessSearchProperties.encryptedProperties,
    ],
  }),
);

export const rewardsAccountMinimumRequirementStateSelector = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccountMinimumRequirementState =>
    getRewardsAccountMinimumRequirementState(rewardsAccounts),
);

export const getOffersRequest = createSelector(
  getPremierCollectionShopSelectedAvailability,
  getSearchedNightCount,
  getFromDate,
  getUntilDate,
  getPremierCollectionShopChosenProduct,
  getPriceQuote,
  (
    selectedLodging,
    nightCount,
    fromDate,
    untilDate,
    chosenProduct,
    priceQuote,
  ): OffersForProductsRequest | null => {
    if (
      selectedLodging &&
      nightCount &&
      fromDate &&
      untilDate &&
      chosenProduct
    ) {
      return {
        products: [
          {
            AvailableProduct: AvailableProductEnum.Lodging,
            id: selectedLodging?.lodging.id,
            merchant: MerchantOfRecord.Hopper,
            lengthOfStay: nightCount,
            stayPeriod: {
              from: dayjs(fromDate).format("YYYY-MM-DD"),
              until: dayjs(untilDate).format("YYYY-MM-DD"),
            },
            isPreferred: selectedLodging?.isPreferred,
            isFreeCancel: selectedLodging?.isFreeCancel,
            collectionType: selectedLodging?.lodgingCollection,
            belongsToPackage: false,
            productPricing: {
              productClass: ProductClassEnum.Economy,
              currency:
                selectedLodging.price?.totalPrice.fiat.currencyCode ?? "USD",
              basePrice:
                chosenProduct.totalDiscountAware.priceWithNoDiscounts.fiat
                  .value,
              grossBookValue:
                chosenProduct.totalDiscountAware.priceWithUnmanagedDiscounts
                  .fiat.value,
              ancillaryTotal: 0, // hardcoded bc Tysons does this https://github.com/hopper-org/Tysons/blob/1167df0bdaa8ae2a5255ea0203438deacff071ae/server/src/main/scala/com/hopper/tysons/service/hotels/availability/converter/ImpFastAvailabilityResponseConverter.scala#L552
              preDiscountTotal:
                priceQuote?.pricing.payNowTotal.fiat.value ??
                chosenProduct.total.fiat.value,
              preDiscountGrandTotal:
                priceQuote?.pricing.tripTotal.fiat.value ??
                chosenProduct.tripTotal.fiat.value,
              sellPrice:
                priceQuote?.pricing.tripTotal.fiat.value ??
                chosenProduct.tripTotal.fiat.value,
            },
          },
        ],
      };
    }
    return null;
  },
);

export const getRewardAccountsWithPaymentAccounts = createSelector(
  getRewardsAccounts,
  getPaymentMethods,
  (rewardsAccounts, paymentMethods) => {
    const accountsWithSavedPayment = paymentMethods.reduce(
      (savedPayments, currentAcc) => {
        const matchingRewardsAcct = rewardsAccounts.find(
          (rewards) =>
            rewards.lastFour === currentAcc.last4 ||
            rewards.lastFourVirtualCardNumbers?.includes(
              currentAcc.last4 || "",
            ),
        );

        if (matchingRewardsAcct) {
          savedPayments.push(matchingRewardsAcct);
        }
        const isTestCard =
          window.__mclean_env__.ENV !== "production" &&
          currentAcc.last4 &&
          TEST_CARD_LAST_FOURS.includes(currentAcc.last4);
        if (isTestCard) {
          savedPayments.push(rewardsAccounts[0]);
        }

        return savedPayments;
      },
      [] as RewardsAccount[],
    );
    return accountsWithSavedPayment;
  },
);

export const getEarnValuesByRewardAcctId = (state: IStoreState) =>
  state.premierCollectionBook.earnValuesByRewardAcctId;
